/**
 * サーバーに届くかを見張り、届かないときは通信を待たずにすぐ失敗させる（機能フラグ crew_fast_offline）。
 *
 * 背景（2026-10-19 紺星丸）:
 *   沖では端末が船内Wi-Fi（インターネットには出られない）につながったままになる。この状態でも
 *   navigator.onLine は true なので、アプリはサーバーへ通信しに行き、タイムアウトの無い fetch が
 *   OSの再送が尽きるまで（1件あたり数分）待っていた。さらにブラウザは同じ相手への同時接続を6本までに
 *   絞るため、起動時・定期確認の通信が順番待ちで積み上がり、起動や打刻に15分かかっていた。
 *
 * やること（サーバー宛ての通信だけ。開発者専用アプリ localhost:7777 などには触らない）:
 *   1. 起動直後に /api/health へ軽い確認を1回（最大4秒）。結果が出るまでの通信はそれを待つ。
 *   2. 届かないと分かっている間は、サーバー宛ての fetch を即座に TypeError で失敗させる。
 *      各画面はこれまでどおり「通信エラー＝圏外」として、保存済みの表示・打刻の端末保存・
 *      端末内の情報でのログインへ進む（待ち時間が無くなるだけで、振る舞いは変えない）。
 *   3. 各通信にタイムアウト（読み込み12秒・送信30秒）。時間切れも TypeError にする
 *      （AbortError のままだと打刻画面が「通信エラー」と見なさず、端末に保存されない）。
 *   4. 通信が失敗したら確認をやり直し、届かなければ「圏外」に切り替える。圏外の間は15秒ごとに確認し、
 *      届いたら window に online を送って、既存の自動送信（打刻・勤怠修正）を動かす。
 */

type State = "unknown" | "up" | "down";

const PROBE_TIMEOUT_MS = 4_000;
const RECHECK_MS = 15_000;
const READ_TIMEOUT_MS = 12_000;
const SEND_TIMEOUT_MS = 30_000;
/** 返事が来ないままこの時間が過ぎたら、サーバーに届くかを確かめる（届かなければその場で打ち切る） */
const WATCHDOG_MS = 4_000;

let installed = false;
let state: State = "unknown";
let probing: Promise<boolean> | null = null;
let recheckTimer: ReturnType<typeof setInterval> | null = null;
let origFetch: typeof fetch;
let apiPrefix = "";
let healthUrl = "";
let selfDispatch = false;
let beforeOnline: (() => Promise<void>) | null = null;
let recoveringUntil = 0;
const BEFORE_ONLINE_MAX_MS = 8_000;
const RECOVERY_GRACE_MS = 20_000;

/** 見張りが入っているか */
export function isNetGateInstalled(): boolean { return installed; }

/**
 * 通信が戻った直後か（この間はトークン無しで401になってもログアウト扱いにしない）。
 * 圏外でのログインは端末内の情報だけで行うのでサーバーのトークンを持っていない。戻った瞬間に
 * 画面の再読み込みとトークンの取り直しが同時に走ると、取り直しより先の通信が401で断られ、
 * ログイン画面へ戻されていた（2026-10-19 再現テストで発覚）。
 */
export function isRecovering(): boolean { return installed && Date.now() < recoveringUntil; }

/** 通信が戻ったとき、online を知らせる前に済ませる処理（トークンの取り直し）を登録する */
export function setBeforeOnline(fn: (() => Promise<void>) | null): void { beforeOnline = fn; }

/** 見張りが入っていて、サーバーに届かないと分かっているときだけ false */
export function isServerReachable(): boolean {
  return !installed || state !== "down";
}

function emit(type: "online" | "offline") {
  selfDispatch = true;
  try { window.dispatchEvent(new Event(type)); } finally { selfDispatch = false; }
  window.dispatchEvent(new CustomEvent("catms-reachability", { detail: { reachable: type === "online" } }));
}

function setState(next: State) {
  if (next === state) return;
  const prev = state;
  state = next;
  if (next === "down") {
    if (!recheckTimer) recheckTimer = setInterval(() => { void probe(); }, RECHECK_MS);
    emit("offline");
  } else if (next === "up") {
    if (recheckTimer) { clearInterval(recheckTimer); recheckTimer = null; }
    if (prev === "down") {
      // 先にトークンを取り直してから online を知らせる（画面の再読み込み・自動送信はその後）
      recoveringUntil = Date.now() + RECOVERY_GRACE_MS;
      const pre = beforeOnline ? beforeOnline().catch(() => undefined) : Promise.resolve();
      void Promise.race([pre, new Promise(r => setTimeout(r, BEFORE_ONLINE_MAX_MS))]).then(() => {
        if (state === "up") emit("online");
      });
    }
  }
}

/** サーバーに届くかを確かめる（同時に1本だけ）。no-cors なので応答の中身・CORS に関係なく「届いたか」だけを見る */
export function probe(): Promise<boolean> {
  if (!installed) return Promise.resolve(true);
  if (typeof navigator !== "undefined" && navigator.onLine === false) { setState("down"); return Promise.resolve(false); }
  if (probing) return probing;
  probing = (async () => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
    try {
      await origFetch(`${healthUrl}?probe=${Date.now()}`, { mode: "no-cors", cache: "no-store", signal: ctrl.signal });
      setState("up");
      return true;
    } catch {
      setState("down");
      return false;
    } finally {
      clearTimeout(timer);
      probing = null;
    }
  })();
  return probing;
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function isApi(url: string): boolean {
  try { return new URL(url, window.location.href).href.startsWith(apiPrefix); } catch { return false; }
}

async function gatedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = urlOf(input);
  if (!isApi(url) || url.startsWith(healthUrl)) return origFetch(input, init);

  if (state === "unknown" || probing) await probe(); // 起動直後・確認中は、確認の結果（最大4秒）を待つ
  if (state === "down") throw new TypeError("Failed to fetch（圏外: サーバーに届きません）");

  const method = String(init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
  const limit = method === "GET" || method === "HEAD" ? READ_TIMEOUT_MS : SEND_TIMEOUT_MS;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), limit);
  const outer = init?.signal ?? (input instanceof Request ? input.signal : undefined);
  if (outer) {
    if (outer.aborted) ctrl.abort();
    else outer.addEventListener("abort", () => ctrl.abort(), { once: true });
  }
  // 電波があった状態から急に届かなくなった場合に、時間切れ（最大30秒）まで待たないための見張り。
  // 回線が遅いだけで届いている（確認が成功する）ならそのまま待つ。
  const watchdog = setTimeout(() => { void probe().then(ok => { if (!ok) ctrl.abort(); }); }, WATCHDOG_MS);
  try {
    const res = await origFetch(input, { ...init, signal: ctrl.signal });
    setState("up");
    return res;
  } catch (e) {
    if (outer?.aborted) throw e;                     // 呼び出し側が自分で止めたものはそのまま返す
    if ((state as State) !== "down") void probe();              // 届かなくなったのかを確かめる（確認で圏外と判定済みならやり直さない）
    throw new TypeError(`Failed to fetch（${ctrl.signal.aborted ? "時間切れ" : "通信できません"}）`);
  } finally {
    clearTimeout(timer);
    clearTimeout(watchdog);
  }
}

/**
 * 見張りを入れる。アプリの描画より前に1回だけ呼ぶ。
 * @param apiBase サーバーのAPIの起点（例: https://api.unison-sea.com/api）
 */
export function installNetGate(apiBase: string): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;
  apiPrefix = new URL(apiBase, window.location.href).href.replace(/\/?$/, "/");
  healthUrl = `${apiPrefix}health`;
  origFetch = window.fetch.bind(window);
  window.fetch = gatedFetch as typeof fetch;

  // 端末が「つながった」と言ったら確かめる（船内Wi-Fiに入っただけかもしれないので鵜呑みにしない）
  window.addEventListener("online", () => {
    if (selfDispatch) return;
    void probe().then(ok => { if (!ok) emit("offline"); });
  });
  window.addEventListener("offline", () => { if (!selfDispatch) setState("down"); });
  // アプリを前面に戻したときも確かめる
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void probe(); });

  void probe();
}
